import type { Permission } from "./permissions";
import type { Relationship, RelationshipUser } from "./relationships";
import { resolveRelationship } from "./relationships";
import { PERMISSION_RULES } from "./rules";

/**
 * ScopeContext — one authorization worldview per request.
 *
 * Today read-scope (list filters) and write/resource-scope (`can()`) are
 * computed by separate, drifting mechanisms (C105–C109, archived primitives trace
 * gap chunk G2):
 *
 *   - `orgFilter` (trpc utils) + the inline accession-list OR build a Prisma
 *     filter from `orgId` / `additionalOrgs` / `splitBillings` / `billToOrgId`.
 *   - `resolveRelationship` + `PERMISSION_RULES` answer `can()` from `orgId`
 *     (ordering) and `receivingOrgId` (lab) ONLY.
 *
 * So an org user linked to an accession via bill-to / shared / split billing
 * **sees** it in the list but `can()` returns false (C105) — list visibility and
 * resource rights disagree, encoded in two places that must be kept in sync by
 * hand (C108).
 *
 * ScopeContext is the single source both derive from: `accessionScope()` yields
 * the read-filter descriptor and `can()` resolves resource rights, and they are
 * built from the **same** relationship resolution over the **same** resource
 * shape — so they cannot diverge.
 *
 * This is a pure module (no Prisma, no session, no DB). The Prisma translation of
 * `accessionScope()` lives in a thin adapter at the trpc boundary. Per the
 * foundation-by-evolution plan it is defined and pinned with contract tests
 * first; the domain foundation roadmap tracks call-site adoption (`orgFilter`,
 * the inline ORs, `PermissionService`) adopt it incrementally afterwards (S1),
 * deleting the legacy paths as they convert (S2).
 *
 * **C105 is resolved by policy, encoded once** (`ScopePolicy.billingLinksGrantRead`).
 */

// ─── Policy (the C105 answer, in one place) ─────────────────────────────────

export type ScopePolicy = {
  /**
   * C105 resolution. When true, an org user linked to an accession only via
   * bill-to / shared / split billing gets READ access (never write): the list
   * keeps showing those accessions and `can()` widens to match, so they agree at
   * the wider set. When false, only ordering / allowed-org links grant access and
   * the list narrows to match `can()`.
   */
  billingLinksGrantRead: boolean;
};

/** Default policy: grant read via billing links (see project decision C105). */
export const DEFAULT_SCOPE_POLICY: ScopePolicy = {
  billingLinksGrantRead: true,
};

// ─── Relationship extension (the unified org axis) ──────────────────────────

/**
 * A billing-link relationship for org users matched only by bill-to / shared /
 * split (not ordering / allowed). It is intentionally read-only — `can()` grants
 * it `read:*` permissions and nothing else.
 */
export type ScopeRelationship = Relationship | "at_billing_org";

/** The full accession org-graph the list filter and `can()` must agree on. */
export type AccessionScopeResource = {
  receivingOrgId?: number | null;
  /** Ordering org. */
  orgId?: number | null;
  billToOrgId?: number | null;
  /** Shared orgs (`additionalOrgs`). */
  additionalOrgIds?: number[];
  /** Split-billing orgs. */
  splitBillingOrgIds?: number[];
};

/**
 * Which accession org-graph **field** a scope matches the user's org ids
 * against. These are fields, not relationships: ordering and allowed-org access
 * both match the same `orgId` field (`orderingOrg`), and which of the two a given
 * org id represents is already decided when the id enters `orgIds` — so the
 * descriptor does not carry that distinction.
 */
export type AccessionOrgLink = "orderingOrg" | "billTo" | "shared" | "split";

const BASE_ORG_LINKS: AccessionOrgLink[] = ["orderingOrg"];
const BILLING_ORG_LINKS: AccessionOrgLink[] = ["billTo", "shared", "split"];

/** The org-link set granted READ under a policy. */
function readableOrgLinks(policy: ScopePolicy): AccessionOrgLink[] {
  return policy.billingLinksGrantRead
    ? [...BASE_ORG_LINKS, ...BILLING_ORG_LINKS]
    : [...BASE_ORG_LINKS];
}

function isMyOrg(user: RelationshipUser, orgId: number | null | undefined) {
  return (
    orgId != null && (orgId === user.org || user.allowedOrgs.includes(orgId))
  );
}

function anyMine(user: RelationshipUser, orgIds: number[] | undefined) {
  return (orgIds ?? []).some((id) => isMyOrg(user, id));
}

/**
 * Resolve the relationship between a user and an accession over its FULL org
 * graph. Lab roles and the ordering / allowed org axis delegate to the existing
 * `resolveRelationship` (unchanged semantics). The new case is `at_billing_org`:
 * an org user whose org/allowedOrgs appear only in bill-to / shared / split,
 * granted when `policy.billingLinksGrantRead` is set.
 */
export function resolveAccessionRelationship(
  user: RelationshipUser,
  resource: AccessionScopeResource,
  policy: ScopePolicy = DEFAULT_SCOPE_POLICY,
): ScopeRelationship | null {
  const base = resolveRelationship(user, resource);
  if (base) {
    return base;
  }

  // Only org roles reach the billing-link extension; lab roles always resolve
  // above (at_owning_lab / at_other_lab / at_any).
  if (!user.role.startsWith("blis_org")) {
    return null;
  }

  if (
    policy.billingLinksGrantRead &&
    (isMyOrg(user, resource.billToOrgId) ||
      anyMine(user, resource.additionalOrgIds) ||
      anyMine(user, resource.splitBillingOrgIds))
  ) {
    return "at_billing_org";
  }

  return null;
}

function isReadPermission(permission: Permission): boolean {
  return permission.startsWith("read:");
}

function ruleGrants(key: string, permission: Permission): boolean {
  const allowed = PERMISSION_RULES[key];
  if (!allowed) {
    return false;
  }
  return allowed.includes("*" as Permission) || allowed.includes(permission);
}

/**
 * Does the resolved relationship grant the permission? Existing
 * role:relationship pairs delegate to `PERMISSION_RULES`.
 *
 * The billing-link relationship (the C105 answer) grants reads only, AND never
 * wider than the role's own-accession read surface: a billing link reads the
 * accession "as if it were your own ordering org", so it grants exactly the read
 * permissions the role already holds at `at_ordering_org`. It must not widen a
 * role's read surface — e.g. an org role that never gets `read:invoices` or
 * `read:audit_logs` anywhere does not get them via a billing link either.
 */
function relationshipGrants(
  role: string,
  relationship: ScopeRelationship,
  permission: Permission,
): boolean {
  if (relationship === "at_billing_org") {
    return (
      isReadPermission(permission) &&
      ruleGrants(`${role}:at_ordering_org`, permission)
    );
  }
  return ruleGrants(`${role}:${relationship}`, permission);
}

// ─── ScopeContext ───────────────────────────────────────────────────────────

export type OrgScope = number[] | "all";

/**
 * A read-filter descriptor for accessions — the Prisma-free shape a boundary
 * adapter translates into a `where` clause. `none` matches nothing; `all` matches
 * everything; `labReceiving` matches `receivingOrgId`; `orgLinks` matches the
 * user's org ids against the named accession fields.
 *
 * `orgLinks` contract: `orgIds` is the already-computed set of org ids where the
 * user holds the permission (the intersection of role grants and the user's
 * orgs); `links` is which accession fields to match those ids against. The two
 * are orthogonal — an accession is in scope when ANY `links` field contains ANY
 * of `orgIds`. The descriptor does not say which id came from which relationship;
 * that is resolved upstream and is not needed to build the filter.
 */
export type AccessionScope =
  | { kind: "all" }
  | { kind: "none" }
  | { kind: "labReceiving"; orgIds: number[] }
  | { kind: "orgLinks"; orgIds: number[]; links: AccessionOrgLink[] };

export type ScopeContext = {
  /** Org ids the user can read (subjects of access). `"all"` for lab staff. */
  readableOrgIds: OrgScope;
  /** Org ids the user can write. Narrower than read; never via billing links. */
  writableOrgIds: OrgScope;
  /** Org ids where the user holds an arbitrary permission. */
  orgIdsFor(permission: Permission): OrgScope;
  /** Does the user hold `permission` on this accession? Unified with the list. */
  can(permission: Permission, resource: AccessionScopeResource): boolean;
  /** The accession read-filter descriptor for `permission` (default read). */
  accessionScope(permission?: Permission): AccessionScope;
};

/**
 * Org ids where the user holds `permission`, via the ordering / allowed / lab
 * axes (the existing `accessibleOrgsFor` semantics, recomputed locally to keep
 * this module self-contained). Billing links are NOT org ids — they are matched
 * by `accessionScope`/`can` against the accession's link fields, so they do not
 * appear here.
 */
function orgIdsForPermission(
  user: RelationshipUser,
  permission: Permission,
): OrgScope {
  const { role } = user;

  if (role === "blis_lab_manager" || role === "blis_lab_admin") {
    return relationshipGrants(role, "at_any", permission) ? "all" : [];
  }

  if (role.startsWith("blis_lab")) {
    const ids: number[] = [];
    if (relationshipGrants(role, "at_owning_lab", permission)) {
      ids.push(user.org);
    }
    if (relationshipGrants(role, "at_other_lab", permission)) {
      return "all";
    }
    return ids;
  }

  if (role.startsWith("blis_org")) {
    const ids: number[] = [];
    if (relationshipGrants(role, "at_ordering_org", permission)) {
      ids.push(user.org);
    }
    if (
      user.allowedOrgs.length > 0 &&
      relationshipGrants(role, "at_allowed_org", permission)
    ) {
      ids.push(...user.allowedOrgs);
    }
    return ids;
  }

  return [];
}

/**
 * Build the per-request authorization worldview for a user. Pure — pass a
 * `RelationshipUser` (the session user is structurally compatible).
 */
export function buildScopeContext(
  user: RelationshipUser,
  policy: ScopePolicy = DEFAULT_SCOPE_POLICY,
): ScopeContext {
  function accessionScope(
    permission: Permission = "read:accessions",
  ): AccessionScope {
    const orgScope = orgIdsForPermission(user, permission);
    if (orgScope === "all") {
      return { kind: "all" };
    }

    if (user.role.startsWith("blis_lab")) {
      return orgScope.length === 0
        ? { kind: "none" }
        : { kind: "labReceiving", orgIds: orgScope };
    }

    if (orgScope.length === 0) {
      return { kind: "none" };
    }

    // Billing links widen READ visibility only (C105). Write/other permissions
    // stay on the ordering / allowed axis.
    const links =
      isReadPermission(permission) && policy.billingLinksGrantRead
        ? readableOrgLinks(policy)
        : [...BASE_ORG_LINKS];
    return { kind: "orgLinks", orgIds: orgScope, links };
  }

  return {
    readableOrgIds: orgIdsForPermission(user, "read:accessions"),
    writableOrgIds: orgIdsForPermission(user, "update:accessions"),
    orgIdsFor: (permission) => orgIdsForPermission(user, permission),
    can: (permission, resource) => {
      const relationship = resolveAccessionRelationship(user, resource, policy);
      if (!relationship) {
        return false;
      }
      return relationshipGrants(user.role, relationship, permission);
    },
    accessionScope,
  };
}
